import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Edit, Trash2, DollarSign } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPlans, deletePlan } from "@/integrations/supabase/services";
import { useToast } from "@/components/ui/use-toast";
import PlanForm from "@/components/plans/PlanForm";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plan } from "@/types/models";
import { formatDateDayMonthYear } from "@/lib/utils";

const Plans = () => {
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  
  const { data: plans = [], isLoading, error } = useQuery({
    queryKey: ['plans', profile?.branch_id],
    queryFn: () => getPlans(profile?.branch_id),
  });
  
  const deleteMutation = useMutation({
    mutationFn: deletePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      toast({
        title: "Plan eliminado",
        description: "El plan ha sido eliminado correctamente.",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al eliminar el plan.",
      });
    },
  });
  
  const filteredPlans = plans.filter(
    (plan) =>
      plan.name.toLowerCase().includes(search.toLowerCase())
  );

  const groupedPlans = filteredPlans.reduce((acc, plan) => {
    const branchName = plan.branch?.name || "Sin Sucursal";
    if (!acc[branchName]) {
      acc[branchName] = [];
    }
    acc[branchName].push(plan);
    return acc;
  }, {} as Record<string, Plan[]>);

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPlan(null);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Planes</h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingPlan(null)}>
                <Plus size={16} />
                Nuevo Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingPlan ? "Editar Plan" : "Nuevo Plan"}
                </DialogTitle>
              </DialogHeader>
              <PlanForm 
                plan={editingPlan} 
                onClose={handleCloseDialog}
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar planes..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white shadow-md rounded-lg overflow-hidden p-6 text-center">
            Cargando planes...
          </div>
        ) : error ? (
          <div className="bg-white shadow-md rounded-lg overflow-hidden p-6 text-center text-red-500">
            Error cargando los planes.
          </div>
        ) : Object.keys(groupedPlans).length === 0 ? (
          <div className="bg-white shadow-md rounded-lg overflow-hidden p-6 text-center">
            No se han creado planes aún.
          </div>
        ) : (
          <Accordion type="single" collapsible className="w-full space-y-4">
            {Object.entries(groupedPlans).map(([branchName, branchPlans]) => (
              <AccordionItem value={branchName} key={branchName} className="bg-white shadow-md rounded-lg">
                <AccordionTrigger className="text-xl font-bold text-gray-800 px-4 py-3">
                  {branchName} ({branchPlans.length})
                </AccordionTrigger>
                <AccordionContent className="px-4 py-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Precio</TableHead>
                        <TableHead>Duración</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Creado</TableHead>
                        <TableHead className="w-[100px]">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {branchPlans.map((plan) => (
                        <TableRow key={plan.id}>
                          <TableCell className="font-medium py-2">{plan.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <DollarSign className="h-4 w-4 mr-1" />
                              {plan.price}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">{plan.time_duration}</TableCell>
                          <TableCell>
                            <Badge
                              variant={plan.active ? "default" : "secondary"}
                              className={plan.active ? "bg-green-500" : ""}
                            >
                              {plan.active ? "Activo" : "Inactivo"}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            {formatDateDayMonthYear(plan.created_at)}
                          </TableCell>
                          <TableCell className="py-2">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleEdit(plan)}
                              >
                                <Edit size={16} />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="icon"
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>¿Eliminar plan?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Esta acción no se puede deshacer. El plan será eliminado permanentemente.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(plan.id)}>
                                      Eliminar
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </MainLayout>
  );
};

export default Plans;